import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Row,
  Select,
  Tag,
  Upload,
} from "antd";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { compressImage, getBase64 } from "../../../../../functions/images";
import { saveComplaintFilesToStorage } from "../../../../../functions/firebase/storage";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { cloneDeep } from "lodash";
import styled from "styled-components";
import QuantityControl from "../../../../../components/shared-components/QuantityControl";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import { formatPrice } from "../../../../../functions/formatters";
import ComplaintModal from "./ComplaintModal";
import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import {
  FIRESTORE_COMPLAINTS_TABLE,
  FIRESTORE_INVOICES_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { updateExactInvoiceCredit } from "../../../../../functions/api/exact";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { calculateBundlePackaging } from "../../../../../utils/calculateBundlePackaging";
import {
  applyCustomerPricingToProduct,
  getBundleItemsWithCustomerPricing,
  getBundlePackagingPrice,
  recalculateBundleItemPricesForOverride,
} from "../../../../../functions/pricing";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { USER_STATUS_ACTIVE } from "../../../../../constants/UserConstant";
import { GROWY } from "../../../../../constants/CompanyConstants";

const { Option } = Select;
const { TextArea } = Input;

const stripUiOnlyCartFields = (cartItem) => {
  if (!cartItem || typeof cartItem !== "object") return cartItem;

  const { finalQuantity, originalQuantity, alreadyCredited, availableForCredit, ...rest } = cartItem;

  return rest;
};

const CreditInvoiceModal = ({ visible, onClose, invoice, users, companyFilter }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const { customers } = useCustomers();
  const { products, getProductById } = useProducts();
  const { priceLists } = usePriceLists();
  const { getCountriesMap } = useCountries();
  const [form] = Form.useForm();
  const isDev = process.env.NODE_ENV === "development";

  // Manual mode detection
  const isManualMode = !invoice;

  // State
  const [loading, setLoading] = useState(false);
  const [creditProducts, setCreditProducts] = useState([]);
  const [packagingLines, setPackagingLines] = useState([]); // Auto-calculated packaging
  const [searchProduct, setSearchProduct] = useState("");
  const [complaintModalVisible, setComplaintModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [complaints, setComplaints] = useState({});
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creditShippingCost, setCreditShippingCost] = useState(false);
  const [manualShippingCostAmount, setManualShippingCostAmount] = useState(0);
  const [manualPriceOverrides, setManualPriceOverrides] = useState({});

  // Refs for tab navigation between product inputs
  const productInputRefs = useRef({});

  // Check if invoice is already processed
  const isInvoiceProcessed = invoice?.invoiceStatus === "processed";
  const isInvoiceDraft = invoice?.status === "draft";
  const isCombinedInvoice = invoice?.isCombinedInvoice === true;

  // Get customer data
  const customer = useMemo(() => {
    if (isManualMode) return selectedCustomer;
    if (!invoice || !customers) return null;
    return customers.find((c) => c.id === invoice.farmorderCustomerId);
  }, [invoice, customers, isManualMode, selectedCustomer]);

  // Check if this is a Growy customer
  const isGrowyCustomer = useMemo(() => {
    if (isManualMode) {
      return selectedCustomer?.company === GROWY;
    }
    return customer?.company === GROWY;
  }, [isManualMode, selectedCustomer, customer]);

  // Use final quantity mode only for Growy customers with existing invoices (not manual mode)
  const useFinalQuantityMode = useMemo(() => {
    return isGrowyCustomer && !isManualMode;
  }, [isGrowyCustomer, isManualMode]);

  // Check if this invoice has existing credits
  const hasExistingCredits = useMemo(() => {
    return invoice?.previousShoppingCarts && invoice.previousShoppingCarts.length > 0;
  }, [invoice]);

  // Original products from invoice (for credit adjustment)
  const originalProducts = useMemo(() => {
    if (isManualMode) return []; // No original products in manual mode
    if (!invoice?.userShoppingCart) return [];

    let originalCart;
    if (hasExistingCredits) {
      const sortedCarts = invoice.previousShoppingCarts.sort((a, b) => a.timestamp - b.timestamp);
      originalCart = sortedCarts[0].items;
    } else {
      originalCart = invoice.userShoppingCart;
    }

    const currentCart = invoice.userShoppingCart;
    const alreadyCreditedMap = new Map();

    if (hasExistingCredits) {
      currentCart.forEach((product) => {
        if (product.itemCount < 0 && product.isCreditLine) {
          const existingCredit = alreadyCreditedMap.get(product.productId) || 0;
          alreadyCreditedMap.set(product.productId, existingCredit + Math.abs(product.itemCount));
        }
      });
    }

    return originalCart.map((product) => {
      const alreadyCredited = alreadyCreditedMap.get(product.productId) || 0;

      return {
        ...product,
        originalQuantity: product.itemCount,
        alreadyCredited: alreadyCredited,
        availableForCredit: Math.max(0, product.itemCount - alreadyCredited),
        // For Growy with existing invoice: store the "final quantity" (what remains on invoice after credit)
        finalQuantity: useFinalQuantityMode ? product.itemCount : undefined,
      };
    });
  }, [invoice, hasExistingCredits, isManualMode, useFinalQuantityMode]);

  // Calculate totals
  const creditSubtotal = useMemo(() => {
    return creditProducts.reduce((sum, product) => {
      const price =
        manualPriceOverrides[product.productId] !== undefined
          ? parseFloat(manualPriceOverrides[product.productId]) || 0
          : parseFloat(product.productPrice) || 0;
      return sum + product.itemCount * price;
    }, 0);
  }, [creditProducts, manualPriceOverrides]);

  // Calculate packaging lines whenever creditProducts changes
  useEffect(() => {
    if (!products || products.length === 0) return;

    const { packagingLines: calculatedPackaging, missingPackaging } = calculateBundlePackaging(
      creditProducts,
      products,
      getProductById,
    );

    setPackagingLines(calculatedPackaging);

    // Show warning notification if packaging is missing
    if (missingPackaging.length > 0) {
      const missingDetails = missingPackaging.map((item) => `${item.bundleName} (${item.packagingId})`).join(", ");

      notification.warning({
        message: t("packaging_not_found_warning_title"),
        description: t("packaging_not_found_warning_description", { products: missingDetails }),
        duration: 0,
        placement: "topRight",
        icon: <InfoCircleOutlined style={{ color: "#faad14" }} />,
      });
    }
  }, [creditProducts, products, getProductById, t]);

  // Calculate packaging subtotal
  const packagingSubtotal = useMemo(() => {
    return packagingLines.reduce((sum, line) => {
      return sum + line.itemCount * line.productPrice;
    }, 0);
  }, [packagingLines]);

  // Calculate shipping cost credit
  const shippingCostCredit = useMemo(() => {
    if (isManualMode) return Number(manualShippingCostAmount || 0);
    if (!creditShippingCost) return 0;
    return Number(invoice?.shippingCost || 0);
  }, [creditShippingCost, invoice, isManualMode, manualShippingCostAmount]);

  // Calculate final total including packaging and shipping cost credit
  const finalTotal = useMemo(() => {
    return creditSubtotal + packagingSubtotal - shippingCostCredit;
  }, [creditSubtotal, packagingSubtotal, shippingCostCredit]);

  // Load existing complaint data when modal opens
  useEffect(() => {
    const loadExistingComplaint = async () => {
      if (visible && invoice && hasExistingCredits && invoice.complaintId) {
        try {
          const complaintRef = doc(
            firestore,
            `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`,
            invoice.complaintId,
          );
          const complaintDoc = await getDoc(complaintRef);

          if (complaintDoc.exists()) {
            const complaintData = complaintDoc.data();

            const existingComplaints = {};
            if (complaintData.products) {
              complaintData.products.forEach((productComplaint) => {
                existingComplaints[productComplaint.product.productId] = {
                  reason: productComplaint.reason,
                  customReason: productComplaint.customReason,
                  department: productComplaint.department,
                  complainingPerson: productComplaint.complainingPerson,
                };
              });
            }

            setComplaints(existingComplaints);

            if (complaintData.description) {
              form.setFieldsValue({ description: complaintData.description });
            }
          }
        } catch (error) {
          console.error("Error loading existing complaint:", error);
        }
      }
    };

    loadExistingComplaint();
  }, [visible, invoice, hasExistingCredits, form]);

  // Reset state when modal opens/closes and initialize products
  useEffect(() => {
    if (visible) {
      if (isManualMode) {
        // Manual mode - start with empty state
        setCreditProducts([]);
        setSearchProduct("");
        setComplaints({});
        setFileList([]);
        setManualShippingCostAmount(0);
        form.resetFields();
      } else if (invoice) {
        // Existing invoice mode - always reset and initialize products
        // For Growy customers with existing invoice, pre-fill all products with finalQuantity = originalQuantity (no credit initially)
        if (useFinalQuantityMode) {
          const preselectedProducts = originalProducts.map((product) => ({
            ...product,
            finalQuantity: product.originalQuantity, // What remains on invoice (no credit = full amount)
            itemCount: 0, // No credit initially
          }));
          setCreditProducts(preselectedProducts);
        } else if (hasExistingCredits && !isInvoiceProcessed) {
          const currentCreditedProducts = originalProducts
            .filter((product) => product.alreadyCredited > 0)
            .map((product) => ({
              ...product,
              itemCount: -product.alreadyCredited,
            }));

          setCreditProducts(currentCreditedProducts);
        } else {
          const preselectedProducts = originalProducts.map((product) => ({
            ...product,
            itemCount: -product.originalQuantity,
          }));

          setCreditProducts(preselectedProducts);
        }

        setSearchProduct("");
        if (!hasExistingCredits) {
          setComplaints({});
        }
        setFileList([]);
        if (!hasExistingCredits) {
          form.resetFields();
        }
      }
    } else {
      // Reset all state when modal closes
      setCreditProducts([]);
      setPackagingLines([]);
      setSearchProduct("");
      setComplaints({});
      setFileList([]);
      setPreviewVisible(false);
      setPreviewImage("");
      setPreviewTitle("");
      setSelectedCustomer(null);
      setCreditShippingCost(false);
      setManualShippingCostAmount(0);
      setManualPriceOverrides({});
      productInputRefs.current = {}; // Reset all input refs
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, invoice?.id, hasExistingCredits, isInvoiceProcessed, isManualMode, useFinalQuantityMode]);

  // Auto-focus first product input for Growy customers with existing invoice when modal opens
  useEffect(() => {
    if (visible && useFinalQuantityMode && creditProducts.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const firstProductId = creditProducts[0]?.productId;
        const firstInputRef = productInputRefs.current[firstProductId];

        if (firstInputRef?.current) {
          firstInputRef.current.focus();
          firstInputRef.current.select();
        }
      }, 100);
    }
  }, [visible, useFinalQuantityMode, creditProducts.length]);

  const handleClose = () => {
    // Reset all state
    setCreditProducts([]);
    setPackagingLines([]);
    setSearchProduct("");
    setComplaints({});
    setFileList([]);
    setPreviewVisible(false);
    setPreviewImage("");
    setPreviewTitle("");
    setSelectedCustomer(null);
    setCreditShippingCost(false);
    setManualShippingCostAmount(0);
    setManualPriceOverrides({});
    form.resetFields();
    onClose();
  };

  // Add product to credit list with negative quantity
  const addProductToCredit = (product) => {
    const existingIndex = creditProducts.findIndex(
      (p) => p.productId === product.id || p.productId === product.productId,
    );

    if (existingIndex >= 0) {
      const newProducts = cloneDeep(creditProducts);

      if (useFinalQuantityMode) {
        // For Growy with existing invoice: decrease the final quantity (what remains on invoice)
        const currentFinal = newProducts[existingIndex].finalQuantity || 0;
        const newFinal = currentFinal - 1;

        newProducts[existingIndex].finalQuantity = newFinal;
        // Calculate credit: originalQuantity - finalQuantity = credit amount (can be negative for credit, positive for addition)
        newProducts[existingIndex].itemCount = -(product.originalQuantity - newFinal);
      } else {
        // Original behavior: make more negative
        const newQuantity = newProducts[existingIndex].itemCount - 1;

        // In manual mode, no limit check needed
        if (!isManualMode && Math.abs(newQuantity) > product.originalQuantity) {
          message.warning(t("notifications.cannot_credit_more_than_original_quantity"));
          return;
        }

        newProducts[existingIndex].itemCount = newQuantity;
      }

      setCreditProducts(newProducts);
    } else {
      // Create new credit product
      if (useFinalQuantityMode) {
        // For Growy with existing invoice: start with final quantity = original - 1
        const originalQty = product.originalQuantity;
        const finalQuantity = originalQty - 1;

        const newProduct = {
          ...product,
          finalQuantity: finalQuantity,
          itemCount: -(originalQty - finalQuantity), // Calculate credit amount
        };

        setCreditProducts([...creditProducts, newProduct]);
      } else {
        // Original behavior (including manual mode)
        let newProduct;
        if (isManualMode) {
          const finalPrice = customer
            ? applyCustomerPricingToProduct(product, customer, priceLists, getCountriesMap())
            : (product.price ?? "0.00").toString();
          const bundleItemsWithPricing =
            product.isBundle && product.bundleItems
              ? getBundleItemsWithCustomerPricing(
                  product,
                  customer,
                  finalPrice,
                  products,
                  priceLists,
                  getCountriesMap(),
                )
              : [];
          newProduct = {
            productId: product.id,
            productName: product.productName,
            externalId: product.exactId?.toString() || "",
            productPrice: finalPrice,
            productBarcode: product.barcode || "",
            productCategory: product.category?.en || "",
            productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
            itemCount: -1,
            orderable: true,
            productVat: product.vat && customer?.country === "NL" ? product.vat[customer.country.toLowerCase()] : 0,
            ...(product.isBundle && {
              isBundle: true,
              bundleItems: bundleItemsWithPricing,
              packagingMaterial: product.packagingMaterial || null,
              hasCustomPrice: product.hasCustomPrice || false,
            }),
          };
        } else {
          newProduct = {
            ...product,
            itemCount: -1,
          };
        }

        setCreditProducts([...creditProducts, newProduct]);
      }
    }
    setSearchProduct("");
  };

  // Increase credit quantity (make more negative)
  const increaseCredit = (product) => {
    if (useFinalQuantityMode) {
      // For Growy with existing invoice: decrease final quantity (what remains on invoice)
      const newProducts = cloneDeep(creditProducts);
      const index = newProducts.findIndex((p) => p.productId === product.productId);

      if (index >= 0) {
        const currentFinal = newProducts[index].finalQuantity || 0;
        const newFinal = currentFinal - 1;

        const originalProduct = originalProducts.find((p) => p.productId === product.productId);
        if (!originalProduct) return;

        newProducts[index].finalQuantity = newFinal;
        newProducts[index].itemCount = -(originalProduct.originalQuantity - newFinal);

        setCreditProducts(newProducts);
      }
    } else {
      // Original behavior (including manual mode)
      if (!isManualMode) {
        const originalProduct = originalProducts.find((p) => p.productId === product.productId);
        if (!originalProduct) return;

        const newProducts = cloneDeep(creditProducts);
        const index = newProducts.findIndex((p) => p.productId === product.productId);

        if (index >= 0) {
          const newQuantity = newProducts[index].itemCount - 1;

          if (Math.abs(newQuantity) > originalProduct.originalQuantity) {
            message.warning(t("notifications.cannot_credit_more_than_original_quantity"));
            return;
          }

          newProducts[index].itemCount = newQuantity;
          setCreditProducts(newProducts);
        }
      } else {
        // Manual mode - no limits
        const newProducts = cloneDeep(creditProducts);
        const index = newProducts.findIndex((p) => p.productId === product.productId);

        if (index >= 0) {
          newProducts[index].itemCount -= 1;
          setCreditProducts(newProducts);
        }
      }
    }
  };

  // Decrease credit quantity (make less negative)
  const decreaseCredit = (product) => {
    const newProducts = cloneDeep(creditProducts);
    const index = newProducts.findIndex((p) => p.productId === product.productId);

    if (index >= 0) {
      if (useFinalQuantityMode) {
        // For Growy with existing invoice: increase final quantity (what remains on invoice)
        const currentFinal = newProducts[index].finalQuantity || 0;
        const newFinal = currentFinal + 1;

        const originalProduct = originalProducts.find((p) => p.productId === product.productId);
        if (!originalProduct) return;

        // Allow final quantity to exceed original (additions are allowed)
        // Only remove product if final quantity equals original (no change)
        if (newFinal === originalProduct.originalQuantity) {
          newProducts.splice(index, 1);
          const newComplaints = { ...complaints };
          delete newComplaints[product.productId];
          setComplaints(newComplaints);
        } else {
          newProducts[index].finalQuantity = newFinal;
          newProducts[index].itemCount = -(originalProduct.originalQuantity - newFinal);
        }
      } else {
        // Original behavior (including manual mode)
        newProducts[index].itemCount += 1;

        if (newProducts[index].itemCount >= 0) {
          newProducts.splice(index, 1);

          // Remove complaint for this product
          const newComplaints = { ...complaints };
          delete newComplaints[product.productId];
          setComplaints(newComplaints);
        }
      }

      setCreditProducts(newProducts);
    }
  };

  // Remove product from credit list
  const removeProduct = (productId) => {
    const newProducts = creditProducts.filter((p) => p.productId !== productId);
    setCreditProducts(newProducts);

    // Remove complaint
    const newComplaints = { ...complaints };
    delete newComplaints[productId];
    setComplaints(newComplaints);
  };

  // Packaging line quantity handlers
  const increasePackagingQuantity = (packagingProductId) => {
    const newPackagingLines = cloneDeep(packagingLines);
    const index = newPackagingLines.findIndex((p) => p.productId === packagingProductId);

    if (index >= 0) {
      newPackagingLines[index].itemCount += 1;
      setPackagingLines(newPackagingLines);
    }
  };

  const decreasePackagingQuantity = (packagingProductId) => {
    const newPackagingLines = cloneDeep(packagingLines);
    const index = newPackagingLines.findIndex((p) => p.productId === packagingProductId);

    if (index >= 0) {
      const newQuantity = newPackagingLines[index].itemCount - 1;

      if (newQuantity <= 0) {
        // Remove packaging line if quantity becomes 0 or negative
        newPackagingLines.splice(index, 1);
      } else {
        newPackagingLines[index].itemCount = newQuantity;
      }

      setPackagingLines(newPackagingLines);
    }
  };

  const handleManualPackagingQuantityInput = (packagingProductId, value) => {
    const newPackagingLines = cloneDeep(packagingLines);
    const index = newPackagingLines.findIndex((p) => p.productId === packagingProductId);

    if (index >= 0) {
      // Ensure positive value
      const quantity = Math.max(0, Math.abs(value));

      if (quantity === 0) {
        // Remove packaging line if quantity is 0
        newPackagingLines.splice(index, 1);
      } else {
        newPackagingLines[index].itemCount = quantity;
      }

      setPackagingLines(newPackagingLines);
    }
  };

  // Handle manual quantity input
  const handleManualQuantityInput = (productId, value) => {
    if (useFinalQuantityMode) {
      // For Growy with existing invoice: input is the final quantity (what remains on invoice)
      const newProducts = cloneDeep(creditProducts);
      const index = newProducts.findIndex((p) => p.productId === productId);

      if (index >= 0) {
        // Ensure positive value
        const finalQuantity = Math.abs(value);

        const originalProduct = originalProducts.find((p) => p.productId === productId);
        if (!originalProduct) return;

        if (finalQuantity === originalProduct.originalQuantity) {
          // Remove product - no change from original
          newProducts.splice(index, 1);
          const newComplaints = { ...complaints };
          delete newComplaints[productId];
          setComplaints(newComplaints);
        } else {
          newProducts[index].finalQuantity = finalQuantity;
          newProducts[index].itemCount = -(originalProduct.originalQuantity - finalQuantity);
        }

        setCreditProducts(newProducts);
      }
    } else {
      // Original behavior (including manual mode)
      if (!isManualMode) {
        const originalProduct = originalProducts.find((p) => p.productId === productId);
        if (!originalProduct) return;

        const newProducts = cloneDeep(creditProducts);
        const index = newProducts.findIndex((p) => p.productId === productId);

        if (index >= 0) {
          if (value < 0 && Math.abs(value) > originalProduct.originalQuantity) {
            message.warning(t("notifications.cannot_credit_more_than_original_quantity"));
            return;
          }

          newProducts[index].itemCount = value;

          if (value >= 0) {
            newProducts.splice(index, 1);

            // Remove complaint
            const newComplaints = { ...complaints };
            delete newComplaints[productId];
            setComplaints(newComplaints);
          }

          setCreditProducts(newProducts);
        }
      } else {
        // Manual mode - no limits, but ensure negative
        const newProducts = cloneDeep(creditProducts);
        const index = newProducts.findIndex((p) => p.productId === productId);

        if (index >= 0) {
          // Force negative value or remove if zero/positive
          if (value > 0) {
            value = -value;
          }

          if (value === 0) {
            newProducts.splice(index, 1);
            const newComplaints = { ...complaints };
            delete newComplaints[productId];
            setComplaints(newComplaints);
          } else {
            newProducts[index].itemCount = value;
          }

          setCreditProducts(newProducts);
        }
      }
    }
  };

  // Open complaint modal for specific product
  const openComplaintModal = (product) => {
    setSelectedProduct(product);
    setComplaintModalVisible(true);
  };

  // Handle complaint save
  const handleComplaintSave = (complaintData) => {
    const newComplaints = { ...complaints };

    // Check if this is a bulk complaint
    if (selectedProduct?.isBulk) {
      // Apply to all credit products
      creditProducts.forEach((product) => {
        newComplaints[product.productId] = complaintData;
      });
    } else {
      // Apply to single product
      newComplaints[selectedProduct.productId] = complaintData;
    }

    setComplaints(newComplaints);
    setComplaintModalVisible(false);
  };

  // Photo upload handlers
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = async (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error(t("notifications.file_type_error"));
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t("notifications.image_size_error"));
      return false;
    }

    try {
      const compressedFile = await compressImage(file, 0.8, 1024);
      return compressedFile;
    } catch (error) {
      console.error("Error compressing image:", error);
      message.error(t("notifications.error_processing_image"));
      return false;
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("upload")}</div>
    </div>
  );

  // Handle paste event for images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          try {
            const compressedFile = await compressImage(file, 0.8, 1024);
            imageFiles.push(compressedFile);
          } catch (error) {
            console.error("Error compressing pasted image:", error);
            message.error(t("notifications.error_processing_image"));
          }
        }
      }
    }

    if (imageFiles.length > 0) {
      const newFileList = imageFiles.map((file, index) => ({
        uid: `paste-${Date.now()}-${index}`,
        name: `pasted-image-${Date.now()}-${index}.png`,
        status: "done",
        originFileObj: file,
      }));

      setFileList([...fileList, ...newFileList]);
      message.success(t("notifications.image_added_successfully") || `${imageFiles.length} image(s) added`);
    }
  };

  // Helper function to create/update complaint
  const handleComplaintDocument = async (formValues) => {
    let complaintId = invoice?.complaintId || null;

    if (Object.keys(complaints).length > 0) {
      const complaintsCollection = collection(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`);

      let uploadedImageUrls = [];
      if (fileList.length > 0) {
        try {
          const fileArray = fileList.map((file) => file.originFileObj);
          const imageUrlArray = [];
          await saveComplaintFilesToStorage("complaints", fileArray, imageUrlArray);
          uploadedImageUrls = imageUrlArray;
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          message.error(t("notifications.error_uploading_files"));
        }
      }

      // Get company code from customer object
      const companyCode = isManualMode
        ? selectedCustomer?.company
        : customers.find((c) => c.id === invoice.farmorderCustomerId)?.company || null;

      // Get company name for backward compatibility
      const companyName = isManualMode ? selectedCustomer?.customerName : invoice.farmorderCustomerName;

      const complaintData = {
        company: companyName,
        companyCode: companyCode,
        farmorderCustomerId: isManualMode ? selectedCustomer.id : invoice.farmorderCustomerId,
        farmorderId: isManualMode ? null : invoice.farmorderIds?.[0] || null,
        invoiceId: isManualMode ? null : invoice.id,
        exactInvoiceId: isManualMode ? null : invoice.exactInvoiceId || null,
        description: formValues.description || "",
        images: uploadedImageUrls,
        products: Object.entries(complaints).map(([productId, complaint]) => {
          const creditedProduct = creditProducts.find((p) => p.productId === productId);
          const quantity = creditedProduct ? Math.abs(creditedProduct.itemCount) : 0;
          const totalAmount = creditedProduct ? quantity * creditedProduct.productPrice : 0;

          return {
            product: {
              productId: productId,
              productName: creditedProduct?.productName || "Unknown Product",
              productPrice: creditedProduct?.productPrice || 0,
            },
            reason: complaint.reason,
            customReason: complaint.customReason || null,
            department: complaint.department,
            complainingPerson: complaint.complainingPerson,
            batchNumber: null,
            quantity: quantity,
            totalAmount: totalAmount,
          };
        }),
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      if (!isManualMode && hasExistingCredits && invoice.complaintId) {
        const complaintRef = doc(
          firestore,
          `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`,
          invoice.complaintId,
        );
        await updateDoc(complaintRef, complaintData);
        complaintId = invoice.complaintId;
      } else {
        complaintData.createdAt = dayjs().unix();
        complaintData.createdBy = email;
        const complaintDoc = await addDoc(complaintsCollection, complaintData);
        complaintId = complaintDoc.id;
      }
    }

    return complaintId || null;
  };

  // Create new credit invoice in Firestore only (for processed invoices)
  const createCreditInvoiceInFirestore = async () => {
    const shippingCost = 0; // Always 0 for credit invoices

    // Combine credit products and packaging lines for shopping cart
    const combinedShoppingCart = [
      ...creditProducts.map((product) => {
        const overridePrice = manualPriceOverrides[product.productId];
        const stripped = stripUiOnlyCartFields(product);
        return {
          ...stripped,
          productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : stripped.productPrice,
          ...(stripped.isBundle &&
            overridePrice && {
              bundleItems: recalculateBundleItemPricesForOverride(stripped, overridePrice),
            }),
          isCreditLine: true,
        };
      }),
      ...packagingLines, // Packaging lines already have correct positive quantities
    ];

    const totalAmount = creditSubtotal + packagingSubtotal + shippingCost;

    const creditInvoiceData = {
      invoiceType: "credit",
      originalInvoiceId: invoice.id,
      farmorderIds: invoice.farmorderIds || [],
      farmorderCustomerId: invoice.farmorderCustomerId,
      farmorderCustomerName: invoice.farmorderCustomerName,
      farmorderDeliveryDate: invoice.farmorderDeliveryDate,
      farmorderUserFirstName: invoice.farmorderUserFirstName,
      farmorderUserLastName: invoice.farmorderUserLastName,
      farmorderRegion: invoice.farmorderRegion || "NL",
      orderDateTime: invoice.orderDateTime,
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      status: "draft",
      userShoppingCart: combinedShoppingCart,
      subTotalAmount: creditSubtotal + packagingSubtotal,
      totalAmount: totalAmount,
      shippingCost: shippingCost.toString(),
      tax: invoice.tax || 0,
      customerDiscount: invoice.customerDiscount || 0,
      discountCodeAmount: 0,
      discountCodePercentage: 0,
      discountCodeApplied: false,
      createdAt: dayjs().unix(),
      createdBy: email,
      creditDescription: form.getFieldValue("description") || "",
      reference: `Credit: ${invoice.exactInvoiceId}`,
      deliveryLabel: invoice.deliveryLabel || "",
    };

    const invoiceRef = doc(collection(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`));
    await setDoc(invoiceRef, creditInvoiceData);

    // Update the original invoice with creditInvoiceId reference
    const originalInvoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
    await updateDoc(originalInvoiceRef, {
      creditInvoiceId: invoiceRef.id,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    });

    return invoiceRef.id;
  };

  // Create manual credit invoice (no original invoice)
  const createManualCreditInvoice = async (formValues) => {
    const shippingCost = manualShippingCostAmount > 0 ? -Number(manualShippingCostAmount) : 0;

    // Combine credit products and packaging lines for shopping cart
    const combinedShoppingCart = [
      ...creditProducts.map((product) => {
        const overridePrice = manualPriceOverrides[product.productId];
        const stripped = stripUiOnlyCartFields(product);
        return {
          ...stripped,
          productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : stripped.productPrice,
          ...(stripped.isBundle &&
            overridePrice && {
              bundleItems: recalculateBundleItemPricesForOverride(stripped, overridePrice),
            }),
          isCreditLine: true,
        };
      }),
      ...packagingLines,
    ];

    const totalAmount = creditSubtotal + packagingSubtotal + shippingCost;

    const user = users?.find((u) => u.userId === formValues.farmorderUserId);

    const creditInvoiceData = {
      invoiceType: "credit",
      farmorderIds: [],
      farmorderCustomerId: selectedCustomer.id,
      farmorderCustomerName: selectedCustomer.customerName,
      farmorderDeliveryDate: formValues.deliveryDate
        ? dayjs(formValues.deliveryDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      farmorderUserFirstName: user?.userFirstName || "",
      farmorderUserLastName: user?.userLastName || "",
      farmorderUserId: user?.userId || "",
      userEmail: user?.userEmail || "",
      userLanguage: user?.userLanguage || "en",
      farmorderRegion: selectedCustomer.customerRegion || "NL",
      orderDateTime: dayjs().unix() * 1000,
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      status: "draft",
      userShoppingCart: combinedShoppingCart,
      subTotalAmount: creditSubtotal + packagingSubtotal,
      totalAmount: totalAmount,
      shippingCost: shippingCost.toString(),
      tax: 0,
      customerDiscount: selectedCustomer.customerSnelstartDiscount || 0,
      discountCodeAmount: 0,
      discountCodePercentage: 0,
      discountCodeApplied: false,
      createdAt: dayjs().unix(),
      createdBy: email,
      creditDescription: form.getFieldValue("description") || "",
      reference: formValues.reference || "",
      deliveryLabel: "",
      paymentMethod: selectedCustomer.paymentMethod || "",
      isManualCreditInvoice: true,
    };

    const invoiceRef = doc(collection(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`));
    await setDoc(invoiceRef, creditInvoiceData);

    return invoiceRef.id;
  };

  // Update draft invoice in Firestore only (no Exact calls)
  const updateDraftInvoiceInFirestore = async () => {
    const previousShoppingCarts =
      invoice.previousShoppingCarts?.length > 0 ? cloneDeep(invoice.previousShoppingCarts) : [];

    previousShoppingCarts.push({
      timestamp: dayjs().unix(),
      items: invoice.userShoppingCart,
      subTotalAmount: invoice.subTotalAmount,
      totalAmount: invoice.totalAmount,
      shippingCost: invoice.shippingCost || 0,
      creditedProducts: invoice.creditedProducts || [],
      creditedAmount: invoice.creditedAmount || 0,
    });

    const newShoppingCart = [];

    if (useFinalQuantityMode) {
      // For Growy: directly modify original product quantities based on finalQuantity
      invoice.userShoppingCart.forEach((product) => {
        if (!product.isCreditLine && !product.isPackagingLine) {
          const modifiedProduct = creditProducts.find((cp) => cp.productId === product.productId);

          if (modifiedProduct) {
            // Product was modified - use finalQuantity
            const updatedProduct = cloneDeep(product);
            updatedProduct.itemCount = modifiedProduct.finalQuantity;
            newShoppingCart.push(updatedProduct);
          } else {
            // Product was not modified - keep original
            newShoppingCart.push(cloneDeep(product));
          }
        }
      });

      // Add any new products that weren't in the original cart (finalQuantity > 0)
      creditProducts.forEach((creditProduct) => {
        const existsInOriginal = invoice.userShoppingCart.some(
          (p) => p.productId === creditProduct.productId && !p.isCreditLine && !p.isPackagingLine,
        );

        if (!existsInOriginal && creditProduct.finalQuantity > 0) {
          newShoppingCart.push({
            productId: creditProduct.productId,
            productName: creditProduct.productName,
            productPrice: creditProduct.productPrice,
            itemCount: creditProduct.finalQuantity,
            productVat: creditProduct.productVat || 0,
          });
        }
      });
    } else {
      // Original behavior for non-Growy customers
      invoice.userShoppingCart.forEach((product) => {
        if (!product.isCreditLine && !product.isPackagingLine) {
          newShoppingCart.push(cloneDeep(product));
        }
      });

      const creditLinesMap = new Map();

      creditProducts.forEach((creditProduct) => {
        const existing = creditLinesMap.get(creditProduct.productId);
        if (existing) {
          // Same product exists - add quantities together (both are negative)
          existing.itemCount += creditProduct.itemCount;
        } else {
          // New product - add to map, preserving bundle data and applying price overrides
          const overridePrice = manualPriceOverrides[creditProduct.productId];
          creditLinesMap.set(creditProduct.productId, {
            ...stripUiOnlyCartFields(creditProduct),
            productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : creditProduct.productPrice,
            ...(creditProduct.isBundle &&
              overridePrice && {
                bundleItems: recalculateBundleItemPricesForOverride(creditProduct, overridePrice),
              }),
            isCreditLine: true,
          });
        }
      });

      creditLinesMap.forEach((creditLine) => {
        if (creditLine.itemCount < 0) {
          newShoppingCart.push(creditLine);
        }
      });
    }

    // Add packaging lines (positive quantities)
    packagingLines.forEach((packagingLine) => {
      newShoppingCart.push(cloneDeep(packagingLine));
    });

    let newSubTotal = newShoppingCart.reduce((sum, product) => {
      return sum + product.itemCount * product.productPrice;
    }, 0);

    // Fix floating point errors
    newSubTotal = Math.abs(newSubTotal) < 0.0001 ? 0 : Math.round(newSubTotal * 100) / 100;

    // For Firestore: save 0 if shipping cost is credited, otherwise keep original
    const shippingCostForFirestore = creditShippingCost ? 0 : Number(invoice.shippingCost || 0);
    const totalAmount = Number(newSubTotal + shippingCostForFirestore);

    const creditedProductsMap = new Map();

    creditProducts.forEach((product) => {
      const originalProduct = originalProducts.find((p) => p.productId === product.productId);
      creditedProductsMap.set(product.productId, {
        productId: product.productId,
        productName: product.productName,
        originalQuantity: originalProduct?.originalQuantity || Math.abs(product.itemCount),
        creditedQuantity: Math.abs(product.itemCount),
      });
    });

    const newCreditedProducts = Array.from(creditedProductsMap.values());
    const newTotalCreditedAmount = creditSubtotal;

    const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
    const updateData = {
      previousShoppingCarts: previousShoppingCarts,
      userShoppingCart: newShoppingCart,
      subTotalAmount: newSubTotal,
      totalAmount: totalAmount,
      shippingCost: shippingCostForFirestore.toString(),
      creditedProducts: newCreditedProducts,
      creditedAmount: newTotalCreditedAmount,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    };

    await updateDoc(invoiceRef, updateData);
  };

  // Update existing open invoice in Exact
  const updateExistingInvoiceInExact = async () => {
    const invoiceStatus = "open";

    if (invoiceStatus === "open") {
      const previousShoppingCarts =
        invoice.previousShoppingCarts?.length > 0 ? cloneDeep(invoice.previousShoppingCarts) : [];

      previousShoppingCarts.push({
        timestamp: dayjs().unix(),
        items: invoice.userShoppingCart,
        subTotalAmount: invoice.subTotalAmount,
        totalAmount: invoice.totalAmount,
        shippingCost: invoice.shippingCost || 0,
        creditedProducts: invoice.creditedProducts || [],
        creditedAmount: invoice.creditedAmount || 0,
      });

      const newShoppingCart = [];
      const creditLinesMap = new Map();

      if (useFinalQuantityMode) {
        // For Growy: directly modify original product quantities based on finalQuantity
        invoice.userShoppingCart.forEach((product) => {
          if (!product.isCreditLine && !product.isPackagingLine) {
            const modifiedProduct = creditProducts.find((cp) => cp.productId === product.productId);

            if (modifiedProduct) {
              // Product was modified - use finalQuantity
              const updatedProduct = cloneDeep(product);
              updatedProduct.itemCount = modifiedProduct.finalQuantity;
              newShoppingCart.push(updatedProduct);
            } else {
              // Product was not modified - keep original
              newShoppingCart.push(cloneDeep(product));
            }
          }
        });

        // Add any new products that weren't in the original cart (finalQuantity > 0)
        creditProducts.forEach((creditProduct) => {
          const existsInOriginal = invoice.userShoppingCart.some(
            (p) => p.productId === creditProduct.productId && !p.isCreditLine && !p.isPackagingLine,
          );

          if (!existsInOriginal && creditProduct.finalQuantity > 0) {
            newShoppingCart.push({
              productId: creditProduct.productId,
              productName: creditProduct.productName,
              productPrice: creditProduct.productPrice,
              itemCount: creditProduct.finalQuantity,
              productVat: creditProduct.productVat || 0,
            });
          }
        });
      } else {
        // Original behavior for non-Growy customers
        invoice.userShoppingCart.forEach((product) => {
          if (!product.isCreditLine && !product.isPackagingLine) {
            newShoppingCart.push(cloneDeep(product));
          }
        });

        creditProducts.forEach((creditProduct) => {
          const existing = creditLinesMap.get(creditProduct.productId);
          if (existing) {
            // Same product exists - add quantities together (both are negative)
            existing.itemCount += creditProduct.itemCount;
          } else {
            // New product - add to map, preserving bundle data and applying price overrides
            const overridePrice = manualPriceOverrides[creditProduct.productId];
            creditLinesMap.set(creditProduct.productId, {
              ...stripUiOnlyCartFields(creditProduct),
              productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : creditProduct.productPrice,
              ...(creditProduct.isBundle &&
                overridePrice && {
                  bundleItems: recalculateBundleItemPricesForOverride(creditProduct, overridePrice),
                }),
              isCreditLine: true,
            });
          }
        });

        creditLinesMap.forEach((creditLine) => {
          if (creditLine.itemCount < 0) {
            newShoppingCart.push(creditLine);
          }
        });
      }

      // Add packaging lines (positive quantities)
      packagingLines.forEach((packagingLine) => {
        newShoppingCart.push(cloneDeep(packagingLine));
      });

      let newSubTotal = newShoppingCart.reduce((sum, product) => {
        return sum + product.itemCount * product.productPrice;
      }, 0);

      // Fix floating point errors
      newSubTotal = Math.abs(newSubTotal) < 0.0001 ? 0 : Math.round(newSubTotal * 100) / 100;

      const shippingCostForFirestore = creditShippingCost ? 0 : Number(invoice.shippingCost || 0);
      const shippingCostForExact = creditShippingCost
        ? -Number(invoice.shippingCost || 0)
        : Number(invoice.shippingCost || 0);
      const totalAmount = Number(newSubTotal + shippingCostForFirestore);

      const creditedProductsMap = new Map();

      creditProducts.forEach((product) => {
        const originalProduct = originalProducts.find((p) => p.productId === product.productId);
        creditedProductsMap.set(product.productId, {
          productId: product.productId,
          productName: product.productName,
          originalQuantity: originalProduct?.originalQuantity || Math.abs(product.itemCount),
          creditedQuantity: Math.abs(product.itemCount),
        });
      });

      const newCreditedProducts = Array.from(creditedProductsMap.values());
      const newTotalCreditedAmount = creditSubtotal;

      try {
        const token = await getFirebaseIDToken();

        let exactLines;
        if (useFinalQuantityMode) {
          // For Growy: send the complete new shopping cart (all products with their final quantities)
          exactLines = newShoppingCart;
        } else {
          // For non-Growy: send only credit lines and packaging
          const creditLinesOnly = Array.from(creditLinesMap.values()).filter((line) => line.itemCount < 0);
          exactLines = [...creditLinesOnly, ...packagingLines];
        }

        const updatedInvoiceForExact = {
          ...invoice,
          userShoppingCart: exactLines,
          subTotalAmount: newSubTotal,
          totalAmount: totalAmount,
          shippingCost: shippingCostForExact.toString(), // Send negative value to Exact for credit
          creditedProducts: newCreditedProducts,
          creditedAmount: newTotalCreditedAmount,
          creditDescription: form.getFieldValue("description") || "",
          creditProducts: creditProducts,
          creditSubtotal: creditSubtotal,
          packagingLines: packagingLines,
          packagingSubtotal: packagingSubtotal,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        };

        await updateExactInvoiceCredit(updatedInvoiceForExact, token, false);
      } catch (exactError) {
        console.error("Error updating invoice in Exact:", exactError);
        console.error("Full error details:", exactError.response?.data || exactError.message);
        message.error(
          t("notifications.error_updating_exact_invoice") +
            ": " +
            (exactError.response?.data?.message || exactError.message),
        );
        throw exactError;
      }

      const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
      const updateData = {
        previousShoppingCarts: previousShoppingCarts,
        userShoppingCart: newShoppingCart,
        subTotalAmount: newSubTotal,
        totalAmount: totalAmount,
        shippingCost: shippingCostForFirestore.toString(),
        creditedProducts: newCreditedProducts,
        creditedAmount: newTotalCreditedAmount,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      await updateDoc(invoiceRef, updateData);
    } else {
      message.error(t("notifications.only_open_invoices_can_be_credited"));
      throw new Error("Only open invoices can be credited");
    }
  };

  // DEV ONLY: Fill mock complaints for all products
  const fillMockComplaints = () => {
    const mockComplaints = {};
    creditProducts.forEach((product) => {
      mockComplaints[product.productId] = {
        reason: "damaged",
        customReason: null,
        department: "logistics",
        complainingPerson: "John Doe",
      };
    });
    setComplaints(mockComplaints);
    message.success("Mock complaints filled for all products");
  };

  // Handle Tab navigation between product inputs
  const handleTabNavigation = (currentProductId, isShiftKey) => {
    const productIds = creditProducts.map((p) => p.productId);
    const currentIndex = productIds.indexOf(currentProductId);

    if (currentIndex === -1) return;

    let nextIndex;
    if (isShiftKey) {
      // Shift+Tab: go to previous
      nextIndex = currentIndex > 0 ? currentIndex - 1 : productIds.length - 1;
    } else {
      // Tab: go to next
      nextIndex = currentIndex < productIds.length - 1 ? currentIndex + 1 : 0;
    }

    const nextProductId = productIds[nextIndex];
    const nextInputRef = productInputRefs.current[nextProductId];

    if (nextInputRef?.current) {
      // For Growy customers (startInManualMode=true), input is already visible
      // Just focus and select
      nextInputRef.current.focus();
      nextInputRef.current.select();
    }
  };

  // Main submit handler
  const handleSubmit = async () => {
    try {
      // Validation
      if (creditProducts.length === 0 && !creditShippingCost && !(isManualMode && manualShippingCostAmount > 0)) {
        message.error(t("notifications.add_at_least_one_product_to_credit"));
        return;
      }

      const productsWithoutComplaints = creditProducts.filter(
        (product) => product.itemCount < 0 && !complaints[product.productId],
      );

      if (productsWithoutComplaints.length > 0) {
        message.error(t("notifications.all_credited_products_must_have_complaint"));
        return;
      }

      // Additional validation for manual mode
      if (isManualMode && !selectedCustomer) {
        message.error(t("notifications.select_customer_first"));
        return;
      }

      const formValues = await form.validateFields();
      setLoading(true);

      let targetInvoiceId = null;

      // Split logic based on mode
      if (isManualMode) {
        // Manual credit invoice - create new standalone credit invoice
        targetInvoiceId = await createManualCreditInvoice(formValues);
        message.success(t("notifications.manual_credit_invoice_created"));
      } else if (isInvoiceProcessed) {
        // Invoice is already processed - create new credit invoice in Firestore only
        targetInvoiceId = await createCreditInvoiceInFirestore();
        message.success(t("notifications.credit_invoice_created_in_firestore"));
      } else if (isInvoiceDraft) {
        // Invoice is still draft - update only in Firestore, skip Exact
        await updateDraftInvoiceInFirestore();
        targetInvoiceId = invoice.id;
        message.success(t("notifications.draft_invoice_updated"));
      } else {
        // Invoice is in_exact - update existing invoice in Exact
        await updateExistingInvoiceInExact();
        targetInvoiceId = invoice.id;
        message.success(t("notifications.credit_invoice_created_successfully"));
      }

      // Handle complaint document only after invoice operation succeeded
      const complaintId = await handleComplaintDocument(formValues);

      if (complaintId !== null && targetInvoiceId) {
        const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, targetInvoiceId);
        await updateDoc(invoiceRef, {
          complaintId,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      }

      handleClose();
    } catch (error) {
      console.error("Error creating credit invoice:", error);
      message.error(t("notifications.error_creating_credit_invoice") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceStatusColor = (status) => {
    if (status === "draft") return "orange";
    if (status === "in_exact") return "green";
    return "default";
  };

  const getInvoiceStatusText = (status) => {
    if (status === "draft") return t("draft");
    if (status === "in_exact") return t("in_exact");
    return status;
  };

  // Render product selection section
  const renderProductSelection = () => {
    // Get product source based on mode
    const productSource = isManualMode ? (products || []).filter((p) => p.productInSeason === true) : originalProducts;

    return (
      <>
        {/* Search for products to add */}
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Select
              style={{ width: "100%", maxWidth: "400px" }}
              placeholder={t("search_product_to_add")}
              showSearch
              value={searchProduct}
              onChange={(value) => {
                if (value) {
                  const product = isManualMode
                    ? products.find((p) => p.id === value)
                    : originalProducts.find((p) => p.productId === value);
                  if (product) {
                    addProductToCredit(product);
                  }
                }
              }}
              filterOption={(input, option) => {
                const product = isManualMode
                  ? products.find((p) => p.id === option.value)
                  : originalProducts.find((p) => p.productId === option.value);
                return product?.productName?.toLowerCase().includes(input.toLowerCase());
              }}
              suffixIcon={<SearchOutlined />}
              disabled={isManualMode && !selectedCustomer}
            >
              {productSource
                .filter((product) => {
                  const productId = isManualMode ? product.id : product.productId;
                  return !creditProducts.some((cp) => cp.productId === productId);
                })
                .map((product) => {
                  const productId = isManualMode ? product.id : product.productId;
                  return (
                    <Option key={productId} value={productId}>
                      {product.productName}
                      {!isManualMode && ` (${t("original")}: ${product.originalQuantity})`}
                    </Option>
                  );
                })}
            </Select>
          </Col>
        </Row>

        {/* Credit Products List */}
        {creditProducts.map((product) => {
          const hasComplaint = complaints[product.productId];
          const originalProduct = !isManualMode
            ? originalProducts.find((p) => p.productId === product.productId)
            : null;
          const effectivePrice =
            manualPriceOverrides[product.productId] !== undefined
              ? parseFloat(manualPriceOverrides[product.productId]) || 0
              : parseFloat(product.productPrice) || 0;

          return (
            <CreditProductItem key={product.productId}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeProduct(product.productId)}
                  style={{ marginTop: 4 }}
                />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: 0, marginBottom: 8 }}>{product.productName}</h4>
                  {/* Price editor */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: 8 }}>
                    {manualPriceOverrides[product.productId] !== undefined ? (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          size="small"
                          style={{ width: "80px" }}
                          prefix="€"
                          value={manualPriceOverrides[product.productId]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || parseFloat(value) >= 0) {
                              setManualPriceOverrides((prev) => ({
                                ...prev,
                                [product.productId]: value,
                              }));
                            }
                          }}
                          autoFocus
                          onBlur={() => {
                            if (!manualPriceOverrides[product.productId]) {
                              setManualPriceOverrides((prev) => {
                                const newOverrides = { ...prev };
                                delete newOverrides[product.productId];
                                return newOverrides;
                              });
                            } else if (product.isBundle) {
                              const packagingPrice = getBundlePackagingPrice(product);
                              if (parseFloat(manualPriceOverrides[product.productId]) < packagingPrice) {
                                setManualPriceOverrides((prev) => ({
                                  ...prev,
                                  [product.productId]: packagingPrice.toFixed(2),
                                }));
                              }
                            }
                          }}
                        />
                        <span style={{ fontSize: "12px", color: "#999", textDecoration: "line-through" }}>
                          €{formatPrice(parseFloat(product.productPrice))}
                        </span>
                        {product.isBundle && getBundlePackagingPrice(product) > 0 && (
                          <span style={{ fontSize: "11px", color: "#888" }}>
                            ({t("packaging")}: €{formatPrice(getBundlePackagingPrice(product))})
                          </span>
                        )}
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setManualPriceOverrides((prev) => {
                              const newOverrides = { ...prev };
                              delete newOverrides[product.productId];
                              return newOverrides;
                            });
                          }}
                          style={{ padding: "0 4px", fontSize: "12px" }}
                        >
                          {t("reset")}
                        </Button>
                      </>
                    ) : (
                      <span
                        onClick={() => {
                          setManualPriceOverrides((prev) => ({
                            ...prev,
                            [product.productId]: product.productPrice,
                          }));
                        }}
                        style={{ fontSize: "12px", cursor: "pointer" }}
                      >
                        €{formatPrice(parseFloat(product.productPrice))}
                      </span>
                    )}
                  </div>
                  <Button
                    size="small"
                    type={hasComplaint ? "default" : "primary"}
                    onClick={() => openComplaintModal(product)}
                  >
                    {hasComplaint ? t("modify_complaint") : t("specify_complaint")}
                  </Button>
                  {hasComplaint && (
                    <Tag color="green" style={{ marginLeft: 4 }}>
                      {t("complaint_specified")}
                    </Tag>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                {!isManualMode && originalProduct && (
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                    {t("originally_ordered")}: {originalProduct.originalQuantity}
                  </div>
                )}

                {useFinalQuantityMode ? (
                  <>
                    {/* Growy with existing invoice UI: Show final quantity (what remains on invoice) as positive number */}
                    <QuantityControl
                      key={`${product.productId}-${visible}`}
                      productId={product.productId}
                      currentQuantity={product.finalQuantity || 0}
                      onIncrease={() => decreaseCredit(product)} // Increase final = less credit
                      onDecrease={() => increaseCredit(product)} // Decrease final = more credit
                      onManualInput={(productId, value) => {
                        handleManualQuantityInput(productId, value);
                      }}
                      allowZero={false}
                      allowNegative={false}
                      inputRef={
                        (productInputRefs.current[product.productId] =
                          productInputRefs.current[product.productId] || React.createRef())
                      }
                      onTabPress={handleTabNavigation}
                      startInManualMode={true}
                    />

                    <div style={{ textAlign: "right", fontSize: "12px", color: "#666" }}>
                      {t("subtotal")}: {Math.abs(product.itemCount)}
                      {" x "}
                      {formatPrice(effectivePrice)}
                      {" = "}
                      {CURRENCY}
                      {formatPrice(Math.abs(product.itemCount) * effectivePrice)}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Original UI (including manual mode): Show credit quantity as negative */}
                    <QuantityControl
                      key={`${product.productId}-${visible}`}
                      productId={product.productId}
                      currentQuantity={product.itemCount}
                      onIncrease={() => increaseCredit(product)}
                      onDecrease={() => decreaseCredit(product)}
                      onManualInput={(productId, value) => {
                        handleManualQuantityInput(productId, value);
                      }}
                      allowZero={false}
                      allowNegative={true}
                      inputRef={
                        (productInputRefs.current[product.productId] =
                          productInputRefs.current[product.productId] || React.createRef())
                      }
                      onTabPress={handleTabNavigation}
                    />

                    <div style={{ textAlign: "right", fontSize: "12px", color: "#666" }}>
                      {t("subtotal")}: {Math.abs(product.itemCount)}
                      {" x "}
                      {formatPrice(effectivePrice)}
                      {" = "}
                      {CURRENCY}
                      {formatPrice(Math.abs(product.itemCount) * effectivePrice)}
                    </div>
                  </>
                )}
              </div>
            </CreditProductItem>
          );
        })}

        {creditProducts.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
            {t("no_products_selected_for_credit")}
          </div>
        )}

        {/* Bulk Complaint Button */}
        {creditProducts.length > 0 && (
          <div
            style={{
              marginTop: 16,
              marginBottom: 16,
              textAlign: "center",
              display: "flex",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <Button
              type="default"
              onClick={() => {
                // Check if any complaints exist
                const hasExistingComplaints = Object.keys(complaints).length > 0;

                if (hasExistingComplaints) {
                  Modal.confirm({
                    title: t("overwrite_complaints_warning"),
                    content: t("overwrite_complaints_message"),
                    okText: t("continue"),
                    cancelText: t("cancel"),
                    onOk: () => {
                      setSelectedProduct({ isBulk: true });
                      setComplaintModalVisible(true);
                    },
                  });
                } else {
                  setSelectedProduct({ isBulk: true });
                  setComplaintModalVisible(true);
                }
              }}
            >
              {t("apply_complaint_to_all_products")}
            </Button>

            {isDev && (
              <Button type="dashed" onClick={fillMockComplaints}>
                Fill Mock Complaints (Dev Only)
              </Button>
            )}
          </div>
        )}

        {/* Packaging Lines Section */}
        {packagingLines.length > 0 && (
          <>
            <Divider style={{ margin: "24px 0" }} />
            <div
              style={{
                padding: "12px 16px",
                marginBottom: "16px",
              }}
            >
              <h6 style={{ margin: "0 0 8px 0", display: "flex", alignItems: "center", gap: 8 }}>
                <InfoCircleOutlined style={{ color: "orange" }} />
                {t("returnable_packaging_automatically_added")}
              </h6>
              <p style={{ margin: 0, color: "#666", fontSize: "13px" }}>{t("returnable_packaging_info")}</p>
            </div>

            {packagingLines.map((packagingLine) => (
              <PackagingLineItem key={packagingLine.productId}>
                <div className={"product-info"}>
                  <div className={"icon-box"}>📦</div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, marginBottom: 4 }}>{packagingLine.productName}</h4>
                  </div>
                </div>

                <div className={"quantity-info"}>
                  <QuantityControl
                    productId={packagingLine.productId}
                    currentQuantity={packagingLine.itemCount}
                    onIncrease={increasePackagingQuantity}
                    onDecrease={decreasePackagingQuantity}
                    onManualInput={handleManualPackagingQuantityInput}
                    allowNegative={false}
                  />

                  <div style={{ textAlign: "right", fontSize: "12px", color: "#666" }}>
                    {t("subtotal")}: {packagingLine.itemCount}
                    {" x "}
                    {formatPrice(packagingLine.productPrice)}
                    {" = "}
                    {CURRENCY}
                    {formatPrice(packagingLine.itemCount * packagingLine.productPrice)}
                  </div>
                </div>
              </PackagingLineItem>
            ))}
          </>
        )}

        {/* Manual Mode: Shipping Cost Credit Section */}
        {isManualMode && (
          <>
            <Divider style={{ margin: "24px 0" }} />
            <div style={{ padding: "12px 16px", marginBottom: "16px" }}>
              <h6 style={{ margin: "0 0 8px 0", fontWeight: 500 }}>{t("manual_shipping_cost_credit")}</h6>
              <p style={{ margin: "0 0 12px 0", color: "#666", fontSize: "13px" }}>
                {t("manual_shipping_cost_credit_instruction")}
              </p>
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={manualShippingCostAmount || undefined}
                onChange={(value) => setManualShippingCostAmount(value || 0)}
                placeholder="0.00"
                addonBefore={`- ${CURRENCY}`}
                style={{ width: 220 }}
                disabled={!selectedCustomer}
              />
            </div>
          </>
        )}

        {/* Shipping Cost Credit Section */}
        {!isManualMode && invoice?.shippingCost && Number(invoice.shippingCost) > 0 && (
          <>
            <Divider style={{ margin: "24px 0" }} />
            <div
              style={{
                padding: "12px 16px",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  id="credit-shipping-cost"
                  checked={creditShippingCost}
                  onChange={(e) => setCreditShippingCost(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                <label
                  htmlFor="credit-shipping-cost"
                  style={{ cursor: "pointer", margin: 0, fontSize: 15, fontWeight: 500 }}
                >
                  {t("credit_shipping_costs")}
                </label>
              </div>
              <p style={{ margin: 0, color: "#666", fontSize: "13px", marginLeft: 30 }}>
                {t("original_shipping_cost")}: {CURRENCY}
                {formatPrice(Number(invoice.shippingCost))}
              </p>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        title={
          <h1 style={{ marginBottom: 20, fontSize: 24 }}>
            {isManualMode ? t("create_manual_credit_invoice") : t("create_credit_invoice")}
          </h1>
        }
        open={visible}
        onCancel={handleClose}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            {t("cancel")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
            {isManualMode
              ? t("create_credit_invoice")
              : isInvoiceProcessed
                ? t("create_credit_invoice")
                : hasExistingCredits
                  ? t("adjust_credit_invoice")
                  : t("create_credit_invoice")}
          </Button>,
        ]}
      >
        <div onPaste={handlePaste} tabIndex={0} style={{ outline: "none" }}>
          {/* Manual Mode: Customer Selection */}
          {isManualMode && (
            <>
              <Form form={form} layout="vertical">
                <Row gutter={16}>
                  <Col lg={12}>
                    <Form.Item
                      name="farmorderCustomerId"
                      label={t("user.company")}
                      rules={[{ required: true, message: t("form.enter_customer_name") }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        placeholder={t("form.choose_company")}
                        onChange={(value) => {
                          const customer = customers.find((c) => c.id === value);
                          setSelectedCustomer(customer);
                        }}
                      >
                        {customers &&
                          [...customers]
                            .filter((company) => {
                              // Filter by companyFilter if not "All"
                              if (companyFilter !== "All") {
                                return company.company === companyFilter;
                              }
                              return true;
                            })
                            .sort((a, b) => {
                              if (!a.customerName || !b.customerName) return 0;
                              return a.customerName.localeCompare(b.customerName);
                            })
                            .map((company) => (
                              <Option key={company.id} value={company.id}>
                                {company.customerName}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item name="farmorderUserId" label={t("user.company_user")} rules={[{ required: false }]}>
                      <Select
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        placeholder={t("form.choose_user")}
                        disabled={!selectedCustomer?.id}
                      >
                        {users &&
                          users
                            .filter((user) => user.userCustomerId === selectedCustomer?.id)
                            .map((user) => (
                              <Option
                                key={user.userId}
                                value={user.userId}
                                disabled={user.userStatus !== USER_STATUS_ACTIVE}
                              >
                                {user.userFirstName} {user.userLastName}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col lg={12}>
                    <Form.Item name="deliveryDate" label={t("delivery_date")}>
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col lg={12}>
                    <Form.Item name="reference" label={t("reference")}>
                      <Input placeholder={t("form.enter_reference")} maxLength={30} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Divider />
            </>
          )}

          {/* Existing Invoice Mode: Customer Information */}
          {!isManualMode && invoice && (
            <>
              <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
                <Descriptions.Item label={t("company")}>{invoice.farmorderCustomerName}</Descriptions.Item>
                <Descriptions.Item label={t("invoice_id")}>{invoice.id}</Descriptions.Item>
                <Descriptions.Item label={t("order_id")}>{invoice.farmorderIds?.[0]}</Descriptions.Item>
                <Descriptions.Item label={t("exact_invoice_id")}>{invoice.exactInvoiceId || "-"}</Descriptions.Item>
                <Descriptions.Item label={t("delivery_date")}>
                  {dayjs(invoice.farmorderDeliveryDate).format("DD-MM-YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label={t("invoice_status")}>
                  <Tag color={getInvoiceStatusColor(invoice.status)}>{getInvoiceStatusText(invoice.status)}</Tag>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* Draft Invoice Info */}
              {isInvoiceDraft && (
                <InfoBox>
                  <h6>{t("draft_invoice_info_title")}</h6>
                  <p>{t("draft_invoice_info_message")}</p>
                </InfoBox>
              )}

              {/* Processed Invoice Warning */}
              {isInvoiceProcessed && (
                <WarningBox>
                  <h6>{t("invoice_already_processed_warning_title")}</h6>
                  <p>{t("invoice_already_processed_warning_message")}</p>
                </WarningBox>
              )}

              {!!hasExistingCredits && !isInvoiceDraft && (
                <InfoBox>
                  <h6>{t("existing_credit_adjustment")}</h6>
                  <p>{t("existing_credit_adjustment_message")}</p>
                </InfoBox>
              )}
            </>
          )}

          {/* Product Selection Header */}
          {!hasExistingCredits && (
            <>
              <h5>{t("credit_products")}</h5>
              <p style={{ color: "#666", marginBottom: "16px" }}>
                {isManualMode ? t("manual_credit_products_instruction") : t("credit_products_instruction")}
              </p>
            </>
          )}

          {/* Show Products */}
          {renderProductSelection()}

          {/* Totals */}
          {(creditProducts.length > 0 ||
            (creditShippingCost && shippingCostCredit > 0 && !isManualMode) ||
            (isManualMode && shippingCostCredit > 0)) && (
            <CreditTotals>
              {creditProducts.length === 0 &&
              ((creditShippingCost && !isManualMode) || (isManualMode && shippingCostCredit > 0)) ? (
                // Shipping-only credit overview
                <>
                  {!isManualMode && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>{t("subtotal")}:</Col>
                      <Col>
                        {CURRENCY}
                        {formatPrice(Number(invoice?.subTotalAmount || 0))}
                      </Col>
                    </Row>
                  )}
                  <Row justify="space-between" style={{ marginBottom: 8 }}>
                    <Col>{t("shipping_cost_credit")}:</Col>
                    <Col style={{ color: "#ff4d4f" }}>
                      - {CURRENCY}
                      {formatPrice(shippingCostCredit)}
                    </Col>
                  </Row>
                  <Divider style={{ margin: "8px 0" }} />
                  <Row justify="space-between">
                    <Col>
                      <strong>{t(isManualMode ? "total_amount" : "new_total")}:</strong>
                    </Col>
                    <Col>
                      <strong>
                        {CURRENCY}
                        {formatPrice(isManualMode ? -shippingCostCredit : Number(invoice?.subTotalAmount || 0))}
                      </strong>
                    </Col>
                  </Row>
                </>
              ) : (
                // Products credit overview (existing behavior)
                <>
                  <Row justify="space-between" style={{ marginBottom: 8 }}>
                    <Col>{t("credit_subtotal")}:</Col>
                    <Col>
                      {CURRENCY}
                      {formatPrice(creditSubtotal)}
                    </Col>
                  </Row>

                  {packagingLines.length > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>{t("packaging_subtotal")}:</Col>
                      <Col style={{ color: "#52c41a" }}>
                        + {CURRENCY}
                        {formatPrice(packagingSubtotal)}
                      </Col>
                    </Row>
                  )}

                  {shippingCostCredit > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>{t("shipping_cost_credit")}:</Col>
                      <Col style={{ color: "#ff4d4f" }}>
                        - {CURRENCY}
                        {formatPrice(shippingCostCredit)}
                      </Col>
                    </Row>
                  )}

                  <Divider style={{ margin: "8px 0" }} />

                  <Row justify="space-between">
                    <Col>
                      <strong>{t("total_amount")}:</strong>
                    </Col>
                    <Col>
                      <strong>
                        {CURRENCY}
                        {formatPrice(finalTotal)}
                      </strong>
                    </Col>
                  </Row>
                </>
              )}
            </CreditTotals>
          )}

          <Divider />

          {/* Credit Description and Photos */}
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label={t("general_description")} rules={[{ required: false }]}>
                  <TextArea rows={4} placeholder={t("enter_complaint_description")} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={t("upload_photos")}>
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    beforeUpload={beforeUpload}
                    multiple
                  >
                    {fileList.length >= 8 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>

      {/* Complaint Modal */}
      <ComplaintModal
        visible={complaintModalVisible}
        onClose={() => setComplaintModalVisible(false)}
        onSave={handleComplaintSave}
        product={selectedProduct}
        existingComplaint={selectedProduct ? complaints[selectedProduct.productId] : null}
      />

      {/* Photo Preview Modal */}
      <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={() => setPreviewVisible(false)}>
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </>
  );
};

const CreditProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 12px;
  background: #fafafa;

  h4 {
    font-size: 16px;
    font-weight: 500;
  }

  .ant-btn-sm {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

const PackagingLineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;

  h4 {
    font-size: 15px;
    font-weight: 500;
  }

  .product-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .icon-box {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .quantity-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .quantity-box {
    padding: 6px 12px;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    min-width: 80px;
    text-align: center;
  }

  .ant-tag {
    margin-right: 4px;
  }
`;

const CreditTotals = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
`;

const InfoBox = styled.div`
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;

  h6 {
    margin: 0 0 8px 0;
    color: #1890ff;
  }

  p {
    margin: 0;
    color: #666;
  }
`;

const WarningBox = styled.div`
  background-color: #fff7e6;
  border: 1px solid #ffa940;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;

  h6 {
    margin: 0 0 8px 0;
    color: #1890ff;
  }

  p {
    margin: 0;
    color: #666;
  }
`;

export default CreditInvoiceModal;
